import { SearchProductsResponse } from "@springtree/eva-services-core";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { join } from "path";
import type { ParsedUrlQuery } from "querystring";
import { renderToString } from "react-dom/server";
import { getServerState } from "react-instantsearch";
import { v4 as uuidv4 } from "uuid";

import JsonLd from "~/components/common/json-ld";
import LayoutPage from "~/components/layout/layout-page";
import ProductGrid from "~/components/products/product-grid";
import ProductGridSkeleton from "~/components/products/product-grid-skeleton";
import CategoryProvider from "~/contexts/category";
import { usePageTracking } from "~/contexts/page-trackings";
import { getCategoryLinks, mapLocale } from "~/services/algolia-utils";
import Contentful from "~/services/contentful";
import Eva from "~/services/eva";
import Logger from "~/services/logger/logger";
import CrossCountry from "~/types/cross-country";
import type { PaginationMeta } from "~/types/pagination";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import evaUtils from "~/utils/eva-utils";
import invariant from "~/utils/invariant";
import { calculateTotalPages, getPageFromQuery } from "~/utils/pagination-utils";
import { extractFilters, getCanonicalUrl, hasActiveFilters } from "~/utils/seo-utils";
import ssrUtils from "~/utils/ssr-utils";
import TrackingUtils from "~/utils/tracking-utils";

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

type BuildPlpPaginationDataParams = {
  locale: string;
  categorySlug: string;
  query: ParsedUrlQuery;
  requestedPage: number;
  totalProducts: number;
  pageSize: number;
  /** When provided (e.g. Algolia nbPages), skips manual calculation */
  totalPages?: number;
};

type BuildPlpPaginationDataResult =
  | { status: "notFound" }
  | { status: "redirect"; destination: string }
  | { status: "ok"; canonicalUrl: string; paginationMeta: PaginationMeta };

function buildPlpPaginationData({
  locale,
  categorySlug,
  query,
  requestedPage,
  totalProducts,
  pageSize,
  totalPages: totalPagesFromApi,
}: BuildPlpPaginationDataParams): BuildPlpPaginationDataResult {
  const totalPages = totalPagesFromApi ?? calculateTotalPages(totalProducts, pageSize);
  const hasFilters = hasActiveFilters(query);
  const preserveFilters = extractFilters(query);
  // Trailing slash is mandatory: `trailingSlash: true` in next.config makes the slash-less URL a 308 redirect,
  // so canonical / prev / next / pagination links must all point to the final URL.
  const baseUrl = `/${locale}/c/${categorySlug}/`;

  if (requestedPage > totalPages && totalPages > 0) {
    // With active filters: redirect to page 1 preserving filters (filters reduced results)
    // Without filters: 404 (user manually entered an invalid page)
    if (hasFilters) {
      const params = new URLSearchParams();
      Object.entries(preserveFilters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(key, v));
        } else {
          params.set(key, value);
        }
      });
      const queryString = params.toString();
      return { status: "redirect", destination: `${baseUrl}${queryString ? `?${queryString}` : ""}` };
    }
    return { status: "notFound" };
  }

  const canonicalUrl = `https://www.kikocosmetics.com${getCanonicalUrl(baseUrl, requestedPage, hasFilters)}`;

  const paginationMeta: PaginationMeta = {
    currentPage: requestedPage,
    totalPages,
    totalProducts,
    pageSize,
    baseUrl,
    hasFilters,
    preserveFilters,
  };

  return {
    status: "ok",
    canonicalUrl,
    paginationMeta,
  };
}

export default function ProductListingPage(props: Props) {
  usePageTracking("category", TrackingUtils.mapTrackCategoryPage(props));
  const categoryFields = props.page.category.fields;
  const reviews = props.reviews?.category;

  const offers = reviews && {
    offers: {
      "@type": "AggregateOffer",
      lowPrice: reviews.lowPrice,
      highPrice: reviews.highPrice,
      priceCurrency: props.reviews?.currency,
      offerCount: reviews.productCount,
    },
  };

  const aggregateRating = reviews &&
    reviews.reviewTotalNumber > 0 && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: reviews?.reviewAverageScore,
        ratingCount: reviews?.reviewTotalNumber,
        bestRating: reviews?.bestRating,
        worstRating: reviews?.worstRating,
      },
    };

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: categoryFields.name,
    description: categoryFields.description,
    url: props.canonicalUrl,
    brand: {
      "@type": "Brand",
      name: Constants.BRAND_NAME,
    },
    ...offers,
    ...aggregateRating,
  };

  return (
    <LayoutPage {...props} navColor="gradient">
      <>
        <JsonLd data={JSON.stringify(productStructuredData)} />
        <CategoryProvider category={props.page.category}>
          <ProductGrid
            components={props.page.components}
            componentsAfterListing={props.page.componentsAfterListing}
            breadcrumbs={props.breadcrumbs}
            categoryLinks={props.categoryLinks}
            navigation={props.navigation}
            changeHeaderLayout={props.page.changeHeaderLayout}
            backgroundColors={props.page.backgroundColors}
            plpCustomHeader={props.page.plpCustomHeader}
            setId={props.page.category.fields.productSet?.fields?.productSetId}
            listScope="plp"
            initialData={props.initialData}
            serverState={props.serverState}
            serverUrl={props.serverUrl}
            algoliaUserToken={props.algoliaUserToken}
            products={props.products}
            pagination={props.pagination}
          />
        </CategoryProvider>
      </>
    </LayoutPage>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  if (context.locale === context.defaultLocale) {
    return {
      notFound: true,
    };
  }

  const categorySlug = (context.params?.category as string[]).join("/");

  // Parse page parameter (default: 1)
  const requestedPage = getPageFromQuery(context.query.page);

  // Redirect ?page=1 to clean URL (SEO: avoid duplicate content)
  if (context.query.page === "1") {
    const { page, ...restQuery } = context.query;
    const params = new URLSearchParams();
    Object.entries(restQuery).forEach(([key, value]) => {
      if (key === "category") return;
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v));
      } else if (value) {
        params.set(key, value);
      }
    });
    const queryString = params.toString();
    const destination = `/${context.locale}/c/${categorySlug}/${queryString ? `?${queryString}` : ""}`;
    return { redirect: { destination, permanent: true } };
  }

  // Get page size from ENV (default: 40)
  const pageSize = parseInt(process.env.NEXT_PUBLIC_PRODUCTS_PER_PAGE || "40", 10);

  const pageData = await ssrUtils.getServerSideProductListingPageData(context, context.draftMode, categorySlug);

  if (!pageData.page) {
    const previewData = await ssrUtils.getServerSideProductListingPreviewPageData(context, context.params?.editorial);

    if (previewData.page?.fallbackUrl) {
      if (previewData.page.fallbackUrl === "NOT_FOUND") {
        return {
          notFound: true,
        };
      }

      let destination = join("/", context.locale!, previewData.page.fallbackUrl, "/");

      return {
        redirect: {
          destination,
          permanent: false,
        },
      };
    } else {
      // The page could not be found on draft mode or it did not have a fallback url.

      return {
        notFound: true,
      };
    }
  }

  if (!context.draftMode) {
    context.res.setHeader("Cache-Control", "public, s-maxage=900, stale-while-revalidate=59");
  }

  if (pageData.page.fallbackUrl) {
    Logger.instance.warn(
      `Detected fallback url for category page ${context.params?.editorial}: ${pageData.page.fallbackUrl}`
    );

    if (pageData.page.fallbackUrl === "NOT_FOUND") {
      return {
        notFound: true,
      };
    }

    let destination = join("/", context.locale!, pageData.page.fallbackUrl, "/");

    return {
      redirect: {
        destination,
        permanent: true,
      },
    };
  }

  const organizationUnitId = pageData.organizationUnitId;
  const algoliaIndexId = mapLocale(
    pageData.configuration["App:Ecommerce:AlgoliaIndexPrefix"] ?? organizationUnitId,
    context.locale
  );
  const categoryId = pageData.page.category.fields.categoryId;
  const productSet = pageData.page.category.fields.productSet?.fields?.productSetId;

  const contentful = new Contentful({ preview: context.draftMode, locale: contentfulUtils.mapLocale(context.locale) });
  const categoryLinksPromise = getCategoryLinks(contentful, algoliaIndexId, 20, categoryId);

  const isEvaProductListing =
    CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_DISABLE_ALGOLIA_PRODUCT_LISTING) || !!productSet;

  if (isEvaProductListing) {
    const eva = new Eva({ organizationUnitId, locale: context.locale });

    let initialDataPromise: Promise<
      Omit<SearchProductsResponse, "Products"> & {
        Products?: CrossCountry.Product[];
      }
    >;

    const initialKey = ["productListing", categoryId, productSet, Eva.DEFAULT_CATEGORY_SORT];

    if (productSet !== undefined) {
      initialDataPromise = evaUtils.getProductsBySetWithChildren(
        eva,
        productSet,
        Eva.DEFAULT_CATEGORY_SORT,
        requestedPage,
        pageSize
      );
    } else if (categoryId) {
      initialDataPromise = evaUtils.getProductsByCategoryWithChildren(
        eva,
        categoryId,
        Eva.DEFAULT_CATEGORY_SORT,
        requestedPage,
        pageSize
      );
    } else {
      invariant(false, "Rendering category page without productSet or categoryId: " + JSON.stringify(context.params));
    }

    const [categoryLinks, initialData] = await Promise.all([categoryLinksPromise, initialDataPromise]);

    const paginationData = buildPlpPaginationData({
      locale: context.locale!,
      categorySlug,
      query: context.query,
      requestedPage,
      totalProducts: initialData.Total || 0,
      pageSize,
    });

    if (paginationData.status === "notFound") {
      return { notFound: true };
    }

    if (paginationData.status === "redirect") {
      return { redirect: { destination: paginationData.destination, permanent: false } };
    }

    return {
      props: {
        ...pageData,
        initialKey: JSON.stringify(initialKey),
        initialData: JSON.stringify(initialData),
        serverState: null,
        serverUrl: null,
        algoliaUserToken: "",
        products: JSON.parse(JSON.stringify(initialData.Products || [])),
        pagination: paginationData.paginationMeta,
        canonicalUrl: paginationData.canonicalUrl,
        categoryLinks,
      },
    };
  } else {
    const protocol = context.req.headers.referer?.split("://")[0] || "https";
    const serverUrl = `${protocol}://${context.req.headers.host}${context.req.url}`;
    const algoliaUserToken = context.req.cookies["algolia-user-token"] || `anonymous-${uuidv4()}`;

    const serverStatePromise = getServerState(
      <ProductGridSkeleton
        serverUrl={serverUrl}
        algoliaIndexId={algoliaIndexId}
        categoryId={categoryId}
        components={pageData.page.components}
        listScope="plp"
        algoliaUserToken={algoliaUserToken}
        pageNumber={requestedPage}
        isPaginationMode={requestedPage > 1}
      />,
      {
        renderToString,
      }
    );

    const [categoryLinks, serverState] = await Promise.all([categoryLinksPromise, serverStatePromise]);

    const algoliaResults = serverState.initialResults[algoliaIndexId]?.results?.[0];

    const paginationData = buildPlpPaginationData({
      locale: context.locale!,
      categorySlug,
      query: context.query,
      requestedPage,
      totalProducts: algoliaResults?.nbHits || 0,
      pageSize,
      totalPages: algoliaResults?.nbPages,
    });

    if (paginationData.status === "notFound") {
      return { notFound: true };
    }

    if (paginationData.status === "redirect") {
      return { redirect: { destination: paginationData.destination, permanent: false } };
    }

    return {
      props: {
        ...pageData,
        serverState,
        serverUrl,
        algoliaUserToken,
        initialData: null,
        products: algoliaResults?.hits || [],
        pagination: paginationData.paginationMeta,
        canonicalUrl: paginationData.canonicalUrl,
        categoryLinks,
      },
    };
  }
}
